// @flow
/* global window */
/* global document */

import * as React from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';

import { Base } from '@gandi/ui';
import { localeSelector, type TranslatorProps } from '@gandi/translate';

import SearchError from './SearchError';
import SearchInput from './SearchInput';
import SearchResults from './SearchResults';
import styles from './DocumentationSearch.scss';
import type { Suggestion } from '../types';
import type { FullState, Dispatch } from '../../../types/index';

type OwnProps = {|
  qbotApi: string,
  qbotAvailableLanguages: Array<string>,
  qbotUrl: string,
  translator: TranslatorProps,
  saveQuestionId: (query: string) => void,
  questionId: string,
|};
type StateProps = {|
  locale: string,
|};

type State = {|
  hasApiError: boolean,
  hasNetworkError: boolean,
  isFetching: boolean,
  isInputFocused: boolean,
  query: string,
  selectedSuggestionIndex: number,
  suggestions: Array<Suggestion>,
|};

type DispatchProps = {|
  dispatch: Dispatch,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
|};
class DocumentationSearch extends React.Component<Props, State> {
  state: State = {
    hasApiError: false,
    hasNetworkError: false,
    isFetching: false,
    isInputFocused: true,
    query: '',
    selectedSuggestionIndex: -1,
    suggestions: [],
  };

  // NOTE:
  // Use case: we send a query that might take a few seconds to
  // get the response from. Meanwhile, we update our query, and
  // another request is sent before getting the response of the
  // previous query. It is necessary to only update our view with
  // the results of our latest query. Due to server latency, the
  // results might not be consistent.
  //
  // Answer: set a request identifier for each request. If some
  // results come from our latest request (by comparing
  // identifiers), then update the view.
  //
  // Alternative: a cleaner way of doing it. A polyfill would be
  // required or both solutions could coexist:
  // https://developer.mozilla.org/en-US/docs/Web/API/AbortController/abort
  requestIdentifier = 0;

  // NOTE: In order to replicate :focus-within behavior using javascript,
  // it is necessary to know whenever a user has focus in a given
  // container. This reference is useful when combined with
  // `handleDocumentClick` which checks for any click that is
  // performed outside of this container. If so, it means that we "lost"
  // the focus. Same goes when the container is blurred (trick is
  // to use `tabIndex` to make it focusable).
  searchContainerRef: ?HTMLElement;

  componentDidMount() {
    document.addEventListener('click', this.handleDocumentClick);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  handleDocumentClick = (event: MouseEvent) => {
    if (
      this.searchContainerRef &&
      this.searchContainerRef instanceof HTMLElement &&
      !this.searchContainerRef.contains((event.target: any))
    ) {
      this.handleInputBlurred();
    }
  };

  handleInputBlurred = (): void => this.setState({ isInputFocused: false });

  handleInputFocused = (): void => this.setState({ isInputFocused: true });

  isLanguageAvailable = (language: string): boolean =>
    this.props.qbotAvailableLanguages.includes(language);

  handleSelectPrevSuggestion = () => {
    this.setState(state => {
      let prevIndex = state.selectedSuggestionIndex - 1;

      if (prevIndex < 0) {
        prevIndex = state.suggestions.length - 1;
      }

      return {
        selectedSuggestionIndex: prevIndex,
      };
    });
  };

  handleSelectNextSuggestion = () => {
    this.setState(state => ({
      selectedSuggestionIndex:
        (state.selectedSuggestionIndex + 1) % state.suggestions.length,
    }));
  };

  handleSelectSuggestion = () => {
    const { selectedSuggestionIndex } = this.state;

    if (selectedSuggestionIndex === -1) {
      return;
    }
    this.handleSendMetrics();
    const suggestion = this.state.suggestions[selectedSuggestionIndex];
    window.open(suggestion.url, '_blank').focus();
  };

  handleClear = () => {
    this.setState({
      query: '',
      selectedSuggestionIndex: -1,
      suggestions: [],
      hasApiError: false,
      hasNetworkError: false,
    });
  };

  handleChange = (event: SyntheticInputEvent<>) => {
    const query = event.target.value;

    if (query.length === 0) {
      this.handleClear();
    } else {
      this.setState({ query });
    }
  };

  handleSearch = () => {
    const { query } = this.state;

    if (query.length === 0) {
      return;
    }

    const lang = this.isLanguageAvailable(this.props.locale)
      ? this.props.locale
      : 'en';

    this.fetchSuggestions(query, lang);
  };

  handleSendMetrics = (rankClick?: number) => {
    const { questionId } = this.props;
    const { selectedSuggestionIndex } = this.state;
    const rank = selectedSuggestionIndex + 1;
    fetch(
      `${this.props.qbotApi}/analytics?question_id=${questionId}&rank=${
        rankClick || rank
      }`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  };

  fetchSuggestions = async (query: string, lang: string) => {
    this.setState({
      isFetching: true,
      hasApiError: false,
      hasNetworkError: false,
    });

    this.requestIdentifier += 1;
    const currentRequestIdentifier = this.requestIdentifier;

    let hasNetworkError = false;
    let hasApiError;
    let suggestions;
    let questionId;

    try {
      let response = await fetch(this.props.qbotUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ lang, query }),
      });
      response = await response.json();
      hasApiError = response.error;
      suggestions = response.suggestions;
      questionId = response.question_id;
      this.props.dispatch(this.props.saveQuestionId(questionId));
    } catch (error) {
      if (
        window &&
        'navigator' in window &&
        'onLine' in window.navigator &&
        !window.navigator.onLine
      ) {
        hasNetworkError = true;
      } else {
        hasApiError = true;
      }
    }

    if (currentRequestIdentifier === this.requestIdentifier) {
      this.setState({
        isFetching: false,
        hasApiError,
        hasNetworkError,
        suggestions: suggestions || [],
        selectedSuggestionIndex: -1,
      });
      this.props.dispatch(this.props.saveQuestionId(questionId || ''));
    }
  };

  getRefCallback = (node: HTMLElement) => {
    this.searchContainerRef = node;
  };

  render(): React.Element<typeof Base> {
    const { locale, translator } = this.props;
    const { __ } = translator;
    const isLocaleAvailable = this.isLanguageAvailable(locale);
    const { isInputFocused } = this.state;
    const hasDropdown =
      isInputFocused &&
      (this.state.hasApiError ||
        this.state.hasNetworkError ||
        this.state.isFetching ||
        this.state.suggestions.length > 0);

    return (
      <Base className={styles.root}>
        <Base
          tagName="header"
          flex
          flexCol
          flexJustifyCenter
          flexAlignItemsCenter
          align="center"
          paddingBottom="base"
          color="white"
          className={styles.header}
        >
          <Base
            tagName="h1"
            id="help-header-search"
            margin="none"
            className={styles.title}
          >
            {__('How can we help you?')}
          </Base>

          {!isLocaleAvailable && (
            <Base
              tagName="h2"
              size="base"
              weight="regular"
              align="center"
              paddingTop="xsmall"
              className={styles.subtitle}
            >
              {__(
                'Our search engine will soon be available in more languages.' +
                  '<for-translators-use>"more languages" needs to be replaced with the translation\'s language (e.g. "will soon be available in Spanish"). It will only be used for languages other than English and French</for-translators-use>'
              )}
            </Base>
          )}
        </Base>

        <Base position="relative" marginBottom="base">
          <Base
            refCallback={this.getRefCallback}
            tabIndex={-1}
            className={clsx(styles.search, {
              [styles.isFocused]: isInputFocused,
              [styles.hasDropdown]: hasDropdown,
            })}
          >
            <SearchInput
              query={this.state.query}
              placeholder={__('How can I...')}
              onChange={this.handleChange}
              onFocus={this.handleInputFocused}
              onSearch={this.handleSearch}
              onSelectSuggestion={this.handleSelectSuggestion}
              onSelectNextSuggestion={this.handleSelectNextSuggestion}
              onSelectPrevSuggestion={this.handleSelectPrevSuggestion}
              inputProps={{
                'aria-labelledby': 'help-header-search',
              }}
            />

            <Base
              position="absolute"
              backgroundColor="grey-light"
              className={styles.results}
            >
              {this.state.hasNetworkError && (
                <SearchError
                  title={__('You seem to be offline')}
                  message={__(
                    'Try using another internet connection, network, or retry with a better signal.'
                  )}
                />
              )}

              {this.state.hasApiError && (
                <SearchError
                  title={__('Service momentarily unavailable')}
                  message={__(
                    'Please feel free to contact our customer support team directly using the form below.'
                  )}
                />
              )}

              <SearchResults
                loading={this.state.isFetching}
                suggestions={this.state.suggestions}
                selectedIndex={this.state.selectedSuggestionIndex}
                onClick={this.handleSendMetrics}
              />
            </Base>
          </Base>
        </Base>
      </Base>
    );
  }
}

const mapStateToProps = (state: FullState): StateProps => ({
  locale: localeSelector(state),
});
// $FlowFixMe[incompatible-call]
export default (connect<Props, StateProps>(mapStateToProps)(
  DocumentationSearch
): React.ComponentType<OwnProps>);
